import React, { useState } from 'react';
import MyBalanceItem from './MyBalanceItem';
import OthersBalanceItem from './OthersBalanceItem';
import { CurrencyCode } from '../constants/currencies';
import { Member } from '../types/member';
import { ChevronDown, ChevronUp, Clipboard } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { formatCurrency } from '../utils/formatters';
import SettlementSummary from './SettlementSummary';
import { SettlementResult, Transaction } from '../utils/settlementCalculator';

interface SettlementResultsProps {
  groupId: string;
  currentMemberId: string;
  members: Member[];
  memberNameMap: Map<string, string>;
  groupCurrency: CurrencyCode;
  treasurerMemberId?: string;
  openInviteModal: () => void;
  allExpenseParticipations: any[] | undefined;
  settlementData: SettlementResult;
  isLoading: boolean;
  isError: boolean;
}

const SettlementResults: React.FC<SettlementResultsProps> = ({ 
  groupId,
  currentMemberId,
  members,
  memberNameMap,
  groupCurrency,
  treasurerMemberId,
  openInviteModal,
  allExpenseParticipations,
  settlementData,
  isLoading,
  isError
 }) => {
  const [isOthersTransactionsOpen, setIsOthersTransactionsOpen] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [isCompleteCopy, setIsCompleteCopy] = useState(false);
  const { myTransactions, othersTransactions } = settlementData;


  const getSettlementResultText = () => {
    let text = '';
    const allTransactions = [...myTransactions, ...othersTransactions];
    // 보내는 사람별로 그룹화
    const transactionsByFrom = new Map<string, Transaction[]>();
    
    allTransactions.forEach((transaction) => {
      const fromId = transaction.from;
      if (!transactionsByFrom.has(fromId)) {
        transactionsByFrom.set(fromId, []);
      }
      transactionsByFrom.get(fromId)!.push(transaction);
    });

    // 각 보내는 사람별로 텍스트 생성
    transactionsByFrom.forEach((transactions, fromId) => {
      const fromName = memberNameMap.get(fromId) || '알 수 없음';
      
      text += `${fromName}님,`;
      transactions.forEach((transaction) => {
        const toName = memberNameMap.get(transaction.to) || '알 수 없음';
        const amount = formatCurrency(transaction.amount, groupCurrency as any);
        text += ` ${toName}님께 ${amount} 송금하세요.\n`;
      });
      text += '\n';
    });

    return text;
  }

  // 송금 내역을 텍스트로 변환하는 함수
  const generateSettlementShareText = () => {
    let text = '엔빵 정산 결과:\n\n';
    text += getSettlementResultText();
    text += '자세한 내역을 보려면 아래 링크에서 확인하세요';
    text += '\nhttps://nbbang.app/groups/' + groupId;
    
    return text;
  };

  // 클립보드에 복사하는 함수
  const copySettlementToClipboard = async () => {
    try {
      const settlementText = generateSettlementShareText();
      await navigator.clipboard.writeText(settlementText);
      setIsCompleteCopy(true);
      setTimeout(() => {
        setIsCompleteCopy(false);
      }, 3000);
      toast.success('정산 결과가 클립보드에 복사되었습니다', {
        duration: 3000,
      });
    } catch (error) {
      console.error('클립보드 복사 실패:', error);
      toast.error('복사에 실패했습니다', {
        duration: 3000,
      });
    }
  };

  if (isLoading || !currentMemberId) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="w-8 h-8 border-4 border-gray-800 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (isError) {
    return <div className='w-full container mx-auto px-4 py-8 text-center text-xl'>데이터를 받아오는데 실패했습니다.</div>;
  }

  return (
    <div className="space-y-4 mb-4">
      {allExpenseParticipations && allExpenseParticipations.length === 0 ? (
        <div className="flex flex-col justify-center items-center my-8 h-full">
          <p className="text-gray-500">등록된 지출 내역이 없어 정산 결과가 없습니다.</p>
          <p className="text-gray-500">하단의 + 버튼을 눌러 지출 내역을 등록해주세요.</p>
        </div>
      ) : (
        <div className='w-full flex flex-col gap-2.5'>
          {myTransactions.length === 0 && othersTransactions.length === 0 ? (
            <div className='py-8 text-center text-lg text-gray-800'>
              👍 모든 정산이 완료되었어요!
            </div>
          ) : myTransactions.length === 0 ? (
            <div className='py-4 text-center text-lg text-gray-800'>
              👍 {memberNameMap.get(currentMemberId)}님은 정산이 완료되었어요!
            </div>
          ) : (
            myTransactions.map((transaction) => (
              <MyBalanceItem
                key={transaction.to+transaction.from+transaction.amount}
                members={members}
                myMemberId={currentMemberId}
                memberNameMap={memberNameMap}
                to={transaction.to}
                from={transaction.from}
                amount={transaction.amount}
                currency={groupCurrency}
                openInviteModal={openInviteModal}
              />
            ))
          )}
          {
            othersTransactions.length > 0 && (
            <div className='py-3 px-4 border border-gray-100 rounded-lg flex flex-col'
              onClick={(e) => {
                setIsOthersTransactionsOpen(!isOthersTransactionsOpen);
                e.stopPropagation();
              }}
            >
              <div className='flex justify-between items-center'>
                  <span className='text-gray-800 font-bold'>다른 멤버간 정산 결과</span>
                  {isOthersTransactionsOpen ? <ChevronUp width={20} height={20} /> : <ChevronDown width={20} height={20} />}
              </div>
              {isOthersTransactionsOpen && (
                <div className='flex flex-col mt-1.5 divide-y divide-gray-100'>
                  {othersTransactions.map((transaction) => (
                    <OthersBalanceItem
                      key={transaction.to+transaction.from+transaction.amount}
                      myMemberId={currentMemberId}
                      memberNameMap={memberNameMap}
                      to={transaction.to}
                      from={transaction.from}
                      amount={transaction.amount}
                      currency={groupCurrency}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
          {
            !!treasurerMemberId &&
            othersTransactions.length === 0 &&
            myTransactions.length > 0 && (
              <div className='py-3 px-4 border border-gray-100 rounded-lg flex flex-col'
                onClick={() => {
                  setIsShareOpen(!isShareOpen);
                }}
              >
                <div className='flex justify-between items-center'>
                    <div className='text-gray-800 font-bold flex items-center gap-1'>{ isShareOpen ? '정산결과 전체 내역:' : '정산결과 전체 복사하기' }</div>
                    {isShareOpen ? <ChevronUp width={20} height={20} /> : <ChevronDown width={20} height={20} />}
                </div>
                {isShareOpen && (
                  <div className='flex flex-col mt-1.5 gap-0.5 text-gray-600 text-sm'>
                    <span className='whitespace-pre-wrap'>{getSettlementResultText()}</span>
                    <button className='w-full flex items-center justify-center gap-1 text-center px-4 py-3 font-semibold text-white bg-blue-500 text-sm rounded-lg -mt-2'
                      onClick={(e) => {
                        e.stopPropagation();
                        copySettlementToClipboard();
                      }}
                      >
                      <Clipboard width={16} height={16} color="white" />
                      <span className='text-white font-bold'>클립보드에 복사{isCompleteCopy ? '완료' : '하기'}</span>
                    </button>
                  </div>
                )}
              </div>
            )
          }
          {myTransactions.length > 0 && (
            <SettlementSummary summary={settlementData.summary} currency={groupCurrency} />
          )}
        </div>
      )}
    </div>
  );
};

export default SettlementResults;
